// Seitenvorlage — EIN Rahmen für alle Verwaltungsseiten.
//
// ⚠️ Vorher gab es keine geteilte Vorlage, an der man die Drift hätte verhindern können — also
// hat jede neu gebaute Seite ihren Rahmen neu erfunden. Nachgemessen waren es vier Muster:
// `max-w-3xl` (768 px, Alben/Interpreten/Playlists), `container mx-auto` (haltepunkt-begrenzt,
// Audio/Video/MindBooks), volle Breite (Dashboard/Inhalte/Themen-Kategorien/Profil) und gar kein
// Container (Mediathek). Sechs davon legten zusätzlich `px-4 py-8` auf das `p-6` der Hülle
// (Layout.tsx → `.page-content`), also doppeltes Polster. Dieselbe Krankheit wie bei den
// Themen-Listen, den 31 handkopierten Tabellenköpfen und den Status-Abzeichen-Funktionen (drei in #636, vier weitere hier):
// es gibt geteilte Bausteine, nur benutzt sie niemand.
//
// **Bewusst OHNE Breiten-Wahlrecht.** Kein `width`/`narrow`/`maxWidth`-Prop. Sobald die Vorlage
// eine Wahl anbietet, hält sich die nächste Seite für den Sonderfall und die Drift ist in drei
// Monaten zurück. Breit ist der Standard (Tom, 2026-08-12); Zielzustand ist der, den Dashboard,
// Mediathek, Themen-Kategorien und Inhalte ohnehin schon fahren.
//
// **Bewusst OHNE eigenes Polster.** Das Polster kommt aus der Hülle (`.page-content` = `p-6` in
// index.css). Eine zweite Polster-Quelle wäre genau der Fehler, der die sechs Seiten doppelt
// eingerückt hat.
//
// **Titelgröße `text-3xl`.** Sechs Seiten fuhren das, zwei (Inhalte, Themen-Kategorien)
// `text-2xl` — Mehrheit gibt den Standard. Untertitel entsprechend `mt-1 text-sm`, wie bei
// Alben/Interpreten/Playlists.
//
// **Nicht umgestellt, begründet:**
// - *Anmelden/Registrieren* (`max-w-md`) — bewusst anders, das sind keine Verwaltungsseiten,
//   sondern zentrierte Formularkarten mit eigenem Look (`.login-page` in index.css).
// - *Dashboard* — Kachelübersicht ohne Titelzeile: seine Kopfzone IST das gebrandete
//   Gradient-Banner mit Signet und Begrüßung, kein Titel-plus-Aktion-Paar. Die Vorlage würde
//   eine zweite Überschrift darüber setzen. Breite und Rhythmus (`space-y-6`, volle Breite,
//   Polster aus der Hülle) sind bereits identisch — es gibt also nichts einzusammeln.
// - *Mediathek* (BunnyCDNManager) — Dateibrowser mit eigener CSS-Insel: ein `<style>`-Block im
//   Bauteil bringt `.manager-header`/`.manager-title` samt eigenem Hintergrund und Polster mit.
//   Ihr Kopf trägt außerdem eine Kennzahlenleiste (Dateien/Größe/Ordner) statt einer Aktion.
//   Eine Umstellung wäre die Auflösung dieser CSS-Insel, nicht das Einsetzen einer Vorlage —
//   eigener Schnitt. (Sie bringt ihr eigenes `padding: 24px` mit, also ebenfalls doppeltes
//   Polster; das gehört in denselben Schnitt.)
import React from 'react';

interface PageLayoutProps {
  /** Seitentitel. Größe ist nicht wählbar — siehe Kommentar oben. */
  title: string;
  /** Einzeiler unter dem Titel. Weglassen, wenn die Seite sich selbst erklärt. */
  subtitle?: string;
  /** Rechte Seite der Titelzeile — in der Regel der „Neu anlegen"-Knopf. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export const PageLayout: React.FC<PageLayoutProps> = ({ title, subtitle, actions, children }) => (
  // `space-y-6` gibt den senkrechten Rhythmus vor. Die umgestellten Seiten tragen deshalb KEIN
  // eigenes `mb-6`/`mb-4` mehr an ihren Blöcken — war unter `space-y-6` ohnehin wirkungslos — die Regel setzt `margin-bottom: 0` auf jedes Nicht-Erst-Kind.
  <div className="space-y-6">
    <div className="flex items-center justify-between gap-4">
      {/* `min-w-0`, damit ein langer Titel den Aktionsknopf nicht aus der Zeile drückt. */}
      <div className="min-w-0">
        <h1 className="text-3xl font-bold text-secondary-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-secondary-600">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
    </div>
    {children}
  </div>
);
